import {
  ClassListType,
  ContactListType,
  ParentListType,
  SchoolListType,
  StudentListType,
  TeacherListType,
} from "utils/interfaces";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getCollectionRequest } from "services/apiRequests";
import { LoadingDots } from "utils/loadingAnimation";
import { MainContext } from "components/utils/Contexts";
import { getUserIdFromToken } from "services/userRequests";
import ClassDashboard from "./ClassDashboard";
import EmptyView from "components/main-view/utils/components/EmptyView";
import AddNewStudent from "components/main-view/utils/create-edit-student";
import SavedSuccessfulPanel from "components/main-view/utils/pop-up-panels/saved-successful-pop-up";
import { MdOutlinePerson } from "react-icons/md";
import { getReports } from "services/classesRequests";
import ClassCard from "components/main-view/utils/cards/ClassCard";
import { singlify } from "utils/utils";
import { usePlatform } from "components/utils/PlatformContext";
import CreateEditClass from "components/main-view/utils/pop-up-panels/create-edit-class-pop-up";
import ViewTeacherList from "components/main-view/utils/pop-up-panels/view-teacher-list-pop-up";

interface CustomerAdminDashboard {
  ref?: any;
  userSchool: SchoolListType | null;
  headerRef: React.MutableRefObject<any>;
  subHeaderRef: React.MutableRefObject<any>;
}

const CustomerAdminDashboard: React.FC<CustomerAdminDashboard> = forwardRef(
  ({ userSchool, headerRef, subHeaderRef }, ref) => {
    // States definition
    const [classes, setClasses] = useState<ClassListType[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedData, setSelectedData] = useState<
      ClassListType | StudentListType | TeacherListType | ParentListType | null
    >(null);
    const [createNewStudent, setCreateNewStudent] = useState(false);
    const [showCreateNewClass, setShowCreateNewClass] = useState(false);
    const [showSavedSuccessfully, setShowSavedSuccessfully] = useState(false);
    // State for view pop up
    const [selectedTeachers, setSelectedTeachers] = useState<ContactListType[]>(
      []
    );
    const [showTeachersList, setShowTeachersList] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // States definition
    const addStudentRef: React.Ref<any> = useRef(null);

    // Get the values from the context
    const { studentsTerminology } = usePlatform();
    const { userRole, contextSchoolByRole, setShowSideBarMenu, userFirstName, userLastName } =
      useContext(MainContext);

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      setSelectedData(data: ClassListType) {
        setSelectedData(data);
        if (data.name !== null)
          subHeaderRef.current.setSubHeaderTitle(data.name);
      },
      onDiscard() {
        onDiscard();
      },
      onBackButtonClick() {
        onDiscard();
      },
      onBackButtonSubHeader() {
        onBackButtonSubHeader();
      },
      setShowCreateNewClass(bool: boolean) {
        setShowCreateNewClass(bool);
      },
      setCreateNewStudent(bool: boolean) {
        setCreateNewStudent(bool);
      },
      handleSaveNewStudent() {
        if (addStudentRef.current) addStudentRef.current.handleSaveNewStudent();
      },
      setSearchQuery(searchQuery: string) {
        setSearchQuery(searchQuery);
      },
      getSelectedData() {
        return selectedData;
      },
      getCreateNewStudent() {
        return createNewStudent;
      },
      onCreateClass() {
        onCreateClass();
      },
      onCreateEditStudent(title?: string, description?: string) {
        onCreateEditStudent(title, description);
      },
      generateReport(format: "csv" | "pdf") {
        generateReport(format);
      },
    }));

    /**
     * Root definition
     */
    useEffect(() => {
      onDiscard();
      getData();
    }, [userSchool]);

    /**
     * Every time is rendered
     */
    useEffect(() => {
      // Display the bar
      subHeaderRef.current.setShow(true);

      // Define the title and description for customer admin and class admin
      if (["customer admin", "class admin"].includes(userRole)) {
        headerRef.current.setHeaderTitle("Dashboard");
        headerRef.current.setHeaderDescription(
          `Welcome, ${userFirstName} ${userLastName}`
        );
      }

      // Set the subheader text
      subHeaderRef.current.setSubHeaderTitle("Overview");
      // Hide the subheader back button
      subHeaderRef.current.setShowBackButton(false);
    }, []);

    /**
     * Function to execute on back click for the sub header button
     */
    const onBackButtonSubHeader = () => {
      // And set any selectedData to null
      setSelectedData(null);

      // Set the subheader text
      subHeaderRef.current.setSubHeaderTitle("Overview");
      // Hide the subheader back button
      subHeaderRef.current.setShowBackButton(false);
      // Hide the generate report button
      headerRef.current.options().setShowGenerateReportsButton(false);
    };

    /**
     * Function that defines the root of this view
     */
    const onDiscard = () => {
      // Set any selectedData to null
      setSelectedData(null);
      // Activate the side bar menu
      setShowSideBarMenu(true);
      // Make sure that the create new student view is not open
      setCreateNewStudent(false);

      // (Main Header) Set titles and back button
      headerRef.current.setHeaderTitle("Dashboard");
      headerRef.current.setHeaderDescription(
        `Welcome, ${userFirstName} ${userLastName}`
      );
      // For the show back button, keep it just when the user is a master admin
      // (we are coming back to the class list view inside a school, so it's
      // necessary to keep showing this button to come back to the school
      // list view.)
      headerRef.current.setShowBackButton(userRole === "master admin");

      // (Dashboard Header)
      // Delete the generate report button
      headerRef.current.options().setShowGenerateReportsButton(false);
      // Activating the create new button
      headerRef.current.options().setShowCreateNewButton(true);
      // And turn off the other buttons
      headerRef.current
        .options()
        .setSaveButton({ show: false, disabled: true });

      // And show the subheader again
      subHeaderRef.current.setShow(true);
      // Set the subheader text
      subHeaderRef.current.setSubHeaderTitle("Overview");
      // Hide the subheader back button
      subHeaderRef.current.setShowBackButton(false);
    };

    /**
     * Show the create new button based on the selectedData state
     */
    useEffect(() => {
      // Keep it hidden when selectedData is not null or undefined
      headerRef.current.options().setShowCreateNewButton(!selectedData);

      // If we have selected data
      if (selectedData) {
        // Change the sub header title
        subHeaderRef.current.setSubHeaderTitle(
          (selectedData as ClassListType).name
        );
      }
    }, [selectedData]);

    /**
     * Function executed when clicking edit a class card
     * @param card
     */
    const handleClassClick = (card: ClassListType) => {
      return (event: React.MouseEvent) => {
        setSelectedData(card);
        subHeaderRef.current.setShowBackButton(true);
        headerRef.current.options().setShowCreateNewButton(false);
        headerRef.current.options().setShowGenerateReportsButton(true);
      };
    };

    /**
     * Function to get the data depending on the class
     * @returns data
     */
    const getData = async () => {
      // Set loading animation
      setIsLoadingData(true);

      // Load the target classes. The configurable schoolId query is only used for master admins
      // (as this dashboard is technically embedded in the master admin dashboard)
      const response = await getCollectionRequest(`/api/classes?schoolId=${
        contextSchoolByRole && contextSchoolByRole._id
      }`);

      // Check if the response is successful
      if (!response.successful) {
        // alert(response.message);
        return;
      }

      // Get the data
      const data = response.content;

      // Assign the data to the corresponding element
      setClasses(data);

      // Stop loading animation
      setIsLoadingData(false);
    };

    /**
     * Function executed after create or update a student
     */
    const afterCreateStudent = () => {
      // Force unselect data
      setSelectedData(null);

      // Show saved successfully pop up
      setShowSavedSuccessfully(true);

      // Discard
      onDiscard();
      // And refresh the data
      getData();
    };

    /**
     * Function executed after create or update a class
     */
    const afterCreateEditClass = () => {
      // Reset the states that could be related to this
      setClasses([]);

      // And refresh the view if we are creating data from the main view
      getData();
    };

    /**
     * Create class function
     */
    const onCreateClass = () => {
      // Open the create class pop up window
      setShowCreateNewClass(true);

      // (Dashboard Header) And close the pop up panel menu
      headerRef.current.options().setShowCreateNewPopUp(false);
    };

    /**
     * Create Student function
     * @param title
     * @param description
     */
    const onCreateEditStudent = (
      title = `New ${singlify(studentsTerminology)}`,
      description = singlify(studentsTerminology)
    ) => {
      // Set the create new student view
      setCreateNewStudent(true);
      // Hide the side bar
      setShowSideBarMenu(false);
      // And close this pop up
      setShowSavedSuccessfully(false);

      // Hide the subheader
      subHeaderRef.current.setShow(false);

      // (Main Header) Set the titles and back button
      headerRef.current.setHeaderTitle(title);
      headerRef.current.setHeaderDescription(
        <div className="w-fit flex gap-1">
          <MdOutlinePerson />
          <div>{description}</div>
        </div>
      );
      headerRef.current.setShowBackButton(true);

      // (Dashboard Header) Set the header buttons
      headerRef.current.options().setShowCreateNewPopUp(false);
      headerRef.current.options().setShowCreateNewButton(false);
      headerRef.current.options().setSaveButton({ show: true, disabled: true });
    };

    /**
     * Function used when generating report
     * @param format
     */
    const generateReport = async (format: "csv" | "pdf") => {
      if (selectedData) {
        getReports(selectedData._id, format);
        headerRef.current.options().setShowGenerateReportsPopUp(false);
      }
    };

    return (
      <>
        <div
          className={`w-full h-full ${
            createNewStudent ? "" : "relative overflow-hidden"
          }`}
        >
          {!createNewStudent && (
            <>
              {classes && classes.length > 0 ? (
                <AnimatePresence initial={false}>
                  {selectedData === null && (
                    <motion.div
                      key={`${selectedData === null}`}
                      initial={{ x: "calc(-100% - 1.5rem)" }}
                      animate={{ x: 0 }}
                      exit={{ x: "-100%" }}
                      transition={{ ease: "easeInOut", duration: 0.5 }}
                      className="absolute w-full h-full"
                    >
                      <div className="w-full h-full flex-col justify-start items-start gap-4 inline-flex overflow-y-auto custom-scroll">
                        {classes &&
                          classes.length > 0 &&
                          classes
                            .filter((classItem: any) => {
                              if (searchQuery.length === 0) return true;
                              return classItem.name.toLowerCase().includes(searchQuery.toLowerCase());
                            })
                            .map((item: ClassListType, index: number) => (
                              <ClassCard
                                key={index}
                                classItem={item}
                                canEdit={false}
                                handleCardClick={handleClassClick(item)}
                                handleDelete={() => {}}
                                setShowTeachersList={setShowTeachersList}
                                setSelectedTeachers={setSelectedTeachers}
                              />
                            ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              ) : !isLoadingData ? (
                <EmptyView
                  showCreateNew={true}
                  onCreateNewClick={() => onCreateClass()}
                />
              ) : (
                <div className="w-full h-full flex justify-center items-center">
                  <LoadingDots bgColor="bg-neutral-300" />
                </div>
              )}
              <AnimatePresence>
                {selectedData !== null && (
                  <motion.div
                    key={`${selectedData === null}`}
                    initial={{ x: "100%" }}
                    animate={{ x: 0 }}
                    exit={{ x: "100%" }}
                    transition={{ ease: "easeInOut", duration: 0.5 }}
                    className="absolute w-full h-full"
                  >
                    <ClassDashboard classData={selectedData as ClassListType} />
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
          {createNewStudent && (
            <AddNewStudent
              ref={addStudentRef}
              selectedData={selectedData as StudentListType}
              afterCreateFunction={afterCreateStudent}
              headerRef={headerRef}
            />
          )}
        </div>
        {/* Definition of the possible pop up panels in this view */}
        <AnimatePresence>
          {/* Create new class pop-up */}
          {showCreateNewClass && (
            <CreateEditClass
              handleClosePopUp={() => setShowCreateNewClass(false)}
              afterCreateEditClass={afterCreateEditClass}
            />
          )}
          {/* Saved successful message after  */}
          {showSavedSuccessfully && (
            <SavedSuccessfulPanel
              title={`${singlify(studentsTerminology)} Added Successfully`}
              paragraphs={[
                `The new ${singlify(studentsTerminology).toLowerCase()} has been successfully added to the system.`,
                `If you wish to add another ${singlify(studentsTerminology).toLowerCase()}, click the 'Add Another' button below.`,
              ]}
              createText="Add another"
              onCreateAnother={() => onCreateEditStudent()}
              onClose={() => setShowSavedSuccessfully(false)}
            />
          )}
          {/* When pressing view all on a card */}
          {/* View all teachers pop-up */}
          {showTeachersList && (
            <ViewTeacherList
              selectedTeachers={selectedTeachers}
              setShowPopUp={setShowTeachersList}
            />
          )}
        </AnimatePresence>
      </>
    );
  }
);

export default CustomerAdminDashboard;
